import { FunctionComponent, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonIcon from '@mui/icons-material/Person';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ListIcon from '@mui/icons-material/List';
import ArticleIcon from '@mui/icons-material/Article';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockIcon from '@mui/icons-material/Lock';
import SortIcon from '@mui/icons-material/Sort';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchFiltersModal from '../../modals/SearchFiltersModal/SearchFiltersModal';
import { searchApi, ApiError } from '../../../services';
import styles from './SearchResults.module.css';

interface SearchResultsData {
  query: string;
  searchType: string;
  totalResults: number;
  categories: {
    lists: any[];
    providers: any[];
    procedures: any[];
    medications: any[];
    articles: any[];
    conditions: any[];
  };
}

interface SearchResultItem {
  id: string;
  type: 'list' | 'provider' | 'procedure' | 'medication' | 'article' | 'condition' | 'specialty';
  relevance: number;
  data: any;
}

const SearchResults: FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState<SearchResultsData | null>(null);
  const [sortedResults, setSortedResults] = useState<SearchResultItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'relevance' | 'cost' | 'rating' | 'distance' | 'rewards'>('relevance');
  const [compareItems, setCompareItems] = useState<Set<string>>(new Set());
  const [dominantType, setDominantType] = useState<'list' | 'provider' | 'procedure' | 'medication' | 'article' | 'condition' | 'specialty' | 'mixed'>('mixed');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['provider']));
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    distance: '10',
    priceRange: { min: 0, max: 10000 },
    rating: 0,
    availability: 'any',
    specialties: [] as string[],
    insuranceAccepted: false,
    facilityType: [] as string[],
    genericOnly: false,
    brandOnly: false,
    resultTypes: [] as string[]
  });
  
  const query = searchParams.get('q') || '';
  const searchType = searchParams.get('type') || 'ai';

  useEffect(() => {
    // Scroll to top when component mounts or search changes
    window.scrollTo(0, 0);
    
    const fetchResults = async () => {
      setIsLoading(true);
      try {
        const responseData = await searchApi.search({
          q: query,
          type: searchType
        });
          setData(responseData);
          
          // Combine all results and sort by relevance
          if (responseData.categories) {
            const allResults: SearchResultItem[] = [];
            
            // Add lists
            responseData.categories.lists?.forEach((item: any) => {
              allResults.push({
                id: item.id,
                type: 'list',
                relevance: item.relevance || 0.95,
                data: item
              });
            });
            
            // Add providers
            responseData.categories.providers?.forEach((item: any, index: number) => {
              allResults.push({
                id: item.id,
                type: 'provider',
                relevance: item.relevance || 0.8,
                data: {
                  ...item,
                  estimatedCost: 45 + (index * 15),
                  savings: 120 - (index * 20),
                  rewards: index === 0 ? 500 : index === 1 ? 250 : 0,
                  featured: index === 0
                }
              });
            });
            
            // Add procedures
            responseData.categories.procedures?.forEach((item: any, index: number) => {
              allResults.push({
                id: item.id,
                type: 'procedure',
                relevance: item.relevance || 0.7,
                data: {
                  ...item,
                  estimatedCost: Math.round(item.averagePrice * 0.3),
                  rewards: index === 0 ? 50 : 0
                }
              });
            });
            
            // Add medications
            responseData.categories.medications?.forEach((item: any, index: number) => {
              allResults.push({
                id: item.id,
                type: 'medication',
                relevance: item.relevance || 0.6,
                data: {
                  ...item,
                  estimatedCost: Math.round(item.averagePrice * 0.2),
                  genericAvailable: index % 2 === 0,
                  genericSavings: 25
                }
              });
            });
            
            // Add conditions
            responseData.categories.conditions?.forEach((item: any) => {
              allResults.push({
                id: item.id,
                type: 'condition',
                relevance: item.relevance || 0.5,
                data: item
              });
            });
            
            // Add specialties/taxonomies
            responseData.categories.specialties?.forEach((item: any) => {
              allResults.push({
                id: item.code,
                type: 'specialty',
                relevance: item.relevance || 0.75,
                data: item
              });
            });
            
            // Add articles
            responseData.categories.articles?.forEach((item: any) => {
              allResults.push({
                id: item.id,
                type: 'article',
                relevance: item.relevance || 0.4,
                data: item
              });
            });
            
            // Initial sort by relevance
            setSortedResults(allResults);
            sortResults(allResults, sortBy);
            
            // Determine dominant result type
            const typeCounts = allResults.reduce((acc, item) => {
              acc[item.type] = (acc[item.type] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
            
            const totalResults = allResults.length;
            const dominantTypeEntry = Object.entries(typeCounts).find(
              ([_, count]) => count / totalResults > 0.7
            );
            
            setDominantType(dominantTypeEntry ? dominantTypeEntry[0] as any : 'mixed');
          }
      } catch (error) {
        console.error('Error fetching search results:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.status, error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (query) {
      fetchResults();
    }
  }, [query, searchType, sortBy]);

  const sortResults = (results: SearchResultItem[], sortType: string) => {
    const sorted = [...results];
    switch (sortType) {
      case 'cost':
        sorted.sort((a, b) => {
          const costA = a.data.averagePrice || a.data.estimatedCost || 999999;
          const costB = b.data.averagePrice || b.data.estimatedCost || 999999;
          return costA - costB;
        });
        break;
      case 'rating':
        sorted.sort((a, b) => (b.data.rating || 0) - (a.data.rating || 0));
        break;
      case 'distance':
        sorted.sort((a, b) => {
          const distA = parseFloat(a.data.distance?.replace(/[^0-9.]/g, '') || '999');
          const distB = parseFloat(b.data.distance?.replace(/[^0-9.]/g, '') || '999');
          return distA - distB;
        });
        break;
      case 'rewards':
        sorted.sort((a, b) => (b.data.rewards || 0) - (a.data.rewards || 0));
        break;
      default: // relevance
        sorted.sort((a, b) => b.relevance - a.relevance);
    }
    setSortedResults(sorted);
  };

  const handleSortChange = (newSort: typeof sortBy) => {
    setSortBy(newSort);
    sortResults(sortedResults, newSort);
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      list: 'Collection',
      provider: 'Provider',
      procedure: 'Procedure',
      medication: 'Medication',
      article: 'Article',
      condition: 'Condition',
      specialty: 'Specialty'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'list':
        return <ListIcon className={styles.typeIcon} />;
      case 'provider':
        return <PersonIcon className={styles.typeIcon} />;
      case 'procedure':
        return <MedicalServicesIcon className={styles.typeIcon} />;
      case 'medication':
        return <MedicationIcon className={styles.typeIcon} />;
      case 'article':
        return <ArticleIcon className={styles.typeIcon} />;
      case 'condition':
        return <LocalHospitalIcon className={styles.typeIcon} />;
      case 'specialty':
        return <LocalHospitalIcon className={styles.typeIcon} />;
      default:
        return null;
    }
  };

  const renderListCard = (list: any) => (
    <div 
      className={styles.resultCard}
      onClick={() => navigate(`/list/${list.id}`)}
    >
      <div className={styles.cardContent}>
        <div className={styles.listHeader}>
          <div className={styles.listInfo}>
            <h3 className={styles.listTitle}>{list.title}</h3>
            <p className={styles.listDescription}>{list.description}</p>
          </div>
        </div>
        <div className={styles.listMeta}>
          <span className={styles.itemCount}>{list.itemCount} items</span>
          <div className={styles.listCategories}>
            {list.categories.slice(0, 2).map((cat: string, index: number) => (
              <span key={index} className={styles.categoryChip}>{cat}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const handleCompareToggle = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newCompareItems = new Set(compareItems);
    if (newCompareItems.has(itemId)) {
      newCompareItems.delete(itemId);
    } else {
      newCompareItems.add(itemId);
    }
    setCompareItems(newCompareItems);
  };

  const toggleSectionExpansion = (sectionType: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(sectionType)) {
      newExpandedSections.delete(sectionType);
    } else {
      newExpandedSections.add(sectionType);
    }
    setExpandedSections(newExpandedSections);
  };

  const renderProviderCard = (provider: any) => {
    const isComparing = compareItems.has(provider.npi);
    
    return (
      <div className={`${styles.resultCard} ${provider.featured ? styles.featured : ''}`}>
        <div className={styles.cardContent}>
          <div className={styles.providerCard}>
            {/* Badge Row */}
            <div className={styles.badgeRow}>
              {provider.featured && (
                <div className={styles.featuredBadge}>
                  <StarIcon style={{ width: 12, height: 12 }} />
                  Mario Pick
                </div>
              )}
              {provider.acceptsInsurance && (
                <span className={styles.networkBadge}>
                  <CheckCircleIcon className={styles.checkIcon} />
                  In-Network
                </span>
              )}
            </div>

            {/* Row 1: Header */}
            <div className={styles.providerHeader}>
              <div className={styles.providerInfo}>
                <div className={styles.providerNameRow}>
                  <h3 className={styles.providerName}>{provider.name}</h3>
                  <span className={styles.providerSpecialty}>• {provider.specialty}</span>
                </div>
                <div className={styles.providerRating}>
                  <StarIcon className={styles.starIcon} />
                  <span className={styles.ratingValue}>{provider.rating}</span>
                  <span className={styles.reviewCount}>({provider.reviewCount})</span>
                </div>
              </div>
            </div>
            
            {/* Row 2: Meta Data */}
            <div className={styles.providerMeta}>
              <div className={styles.metaItem}>
                <LocationOnIcon className={styles.metaIcon} />
                <span>{provider.distance}</span>
              </div>
              <div className={styles.metaItem}>
                <AccessTimeIcon className={styles.metaIcon} />
                <span>Next: {new Date(provider.nextAvailable).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</span>
              </div>
            </div>
            
            {/* Row 3: Cost & Savings */}
            <div className={styles.costSection}>
              <div className={styles.costInfo}>
                <span className={styles.primaryCost}>${provider.estimatedCost || '45'}</span>
                <span className={styles.savingsInfo}>
                  <TrendingDownIcon className={styles.savingsIcon} />
                  Save ${provider.savings || '120'} vs. avg
                </span>
              </div>
              <span className={styles.verifiedPrice} title="Price verified with your insurance">
                <VerifiedIcon className={styles.trustIcon} />
                Verified
              </span>
            </div>
            
            {/* Row 4: Actions */}
            <div className={styles.cardActions}>
              <button 
                className={styles.compareAction}
                onClick={(e) => handleCompareToggle(provider.npi, e)}
              >
                <div className={`${styles.compareCheckbox} ${isComparing ? styles.checked : ''}`}>
                  {isComparing ? <CheckBoxIcon style={{ width: 14, height: 14, color: '#fff' }} /> : null}
                </div>
                Compare
              </button>
              <button 
                className={styles.detailsAction}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/provider/${provider.npi}`);
                }}
              >
                View
                <ChevronRightIcon className={styles.chevronIcon} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProcedureCard = (procedure: any) => {
    const isComparing = compareItems.has(procedure.cpt);
    
    return (
      <div className={styles.resultCard}>
        <div className={styles.cardContent}>
          <div className={styles.procedureCard}>
            {/* Row 1: Header */}
            <div className={styles.procedureHeader}>
              <div className={styles.procedureInfo}>
                <h3 className={styles.procedureName}>{procedure.name}</h3>
                <span className={styles.procedureCategory}>{procedure.category}</span>
              </div>
              {procedure.featured && (
                <span className={styles.networkBadge}>
                  <StarIcon className={styles.checkIcon} />
                  Featured
                </span>
              )}
            </div>
            
            {/* Row 2: Meta Data */}
            <div className={styles.providerMeta}>
              <div className={styles.metaItem}>
                <span>{procedure.providers} providers</span>
              </div>
              <div className={styles.metaItem}>
                <span>Range: ${procedure.priceRange.min}-${procedure.priceRange.max}</span>
              </div>
            </div>
            
            {/* Row 3: Cost & Coverage */}
            <div className={styles.costSection}>
              <div className={styles.costInfo}>
                <span className={styles.primaryCost}>${procedure.estimatedCost || procedure.averagePrice}</span>
                <span className={styles.savingsInfo}>
                  {procedure.insuranceCoverage} covered
                </span>
              </div>
              <span className={styles.verifiedPrice} title="Price verified with your insurance">
                <VerifiedIcon className={styles.trustIcon} />
                Verified
              </span>
            </div>
            
            {/* Row 4: Actions */}
            <div className={styles.cardActions}>
              {(dominantType === 'procedure' || sortedResults.filter(r => r.type === 'procedure').length > sortedResults.length * 0.7) && (
                <button 
                  className={styles.compareAction}
                  onClick={(e) => handleCompareToggle(procedure.cpt, e)}
                >
                  <div className={`${styles.compareCheckbox} ${isComparing ? styles.checked : ''}`}>
                    {isComparing ? <CheckBoxIcon style={{ width: 14, height: 14, color: '#fff' }} /> : null}
                  </div>
                  Compare
                </button>
              )}
              <button 
                className={styles.detailsAction}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/procedure/${procedure.cpt}`);
                }}
                style={{ marginLeft: (dominantType !== 'procedure' && sortedResults.filter(r => r.type === 'procedure').length <= sortedResults.length * 0.7) ? 'auto' : undefined }}
              >
                View
                <ChevronRightIcon className={styles.chevronIcon} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMedicationCard = (medication: any) => {
    const isComparing = compareItems.has(medication.ndc);
    
    return (
      <div className={styles.resultCard}>
        <div className={styles.cardContent}>
          <div className={styles.medicationCard}>
            {/* Row 1: Header */}
            <div className={styles.medicationHeader}>
              <div className={styles.medicationInfo}>
                <h3 className={styles.medicationName}>{medication.name}</h3>
                <span className={styles.medicationCategory}>{medication.category}</span>
              </div>
              {medication.genericAvailable && (
                <span className={styles.networkBadge} style={{ backgroundColor: '#FFF3CD', color: '#856404' }}>
                  Generic Available
                </span>
              )}
            </div>
            
            {/* Row 2: Dosages */}
            <div className={styles.dosages}>
              {medication.commonDosages.slice(0, 3).map((dosage: string, index: number) => (
                <span key={index} className={styles.dosageChip}>{dosage}</span>
              ))}
            </div>
            
            {/* Row 3: Cost & Coverage */}
            <div className={styles.costSection}>
              <div className={styles.costInfo}>
                <span className={styles.primaryCost}>${medication.estimatedCost || medication.averagePrice}</span>
                <span className={styles.savingsInfo}>
                  {medication.insuranceCoverage} covered
                </span>
              </div>
              <span className={styles.verifiedPrice}>
                <LockIcon className={styles.lockIcon} />
                30-day supply
              </span>
            </div>
            
            {/* Row 4: Actions */}
            <div className={styles.cardActions}>
              {(dominantType === 'medication' || sortedResults.filter(r => r.type === 'medication').length > sortedResults.length * 0.7) && (
                <button 
                  className={styles.compareAction}
                  onClick={(e) => handleCompareToggle(medication.ndc, e)}
                >
                  <div className={`${styles.compareCheckbox} ${isComparing ? styles.checked : ''}`}>
                    {isComparing ? <CheckBoxIcon style={{ width: 14, height: 14, color: '#fff' }} /> : null}
                  </div>
                  Compare
                </button>
              )}
              <button 
                className={styles.detailsAction}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/medication/${medication.ndc}`);
                }}
                style={{ marginLeft: (dominantType !== 'medication' && sortedResults.filter(r => r.type === 'medication').length <= sortedResults.length * 0.7) ? 'auto' : undefined }}
              >
                View
                <ChevronRightIcon className={styles.chevronIcon} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderArticleCard = (article: any) => (
    <div 
      className={styles.resultCard}
      onClick={() => window.open(`/article/${article.id}`, '_blank')}
    >
      <div className={styles.cardContent}>
        <div className={styles.articleContent}>
          <h3 className={styles.articleTitle}>{article.title}</h3>
          <p className={styles.articlePreview}>{article.preview}</p>
          <div className={styles.articleMeta}>
            <span className={styles.articleSource}>{article.source}</span>
            <span>• {article.readTime}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderConditionCard = (condition: any) => (
    <div 
      className={styles.resultCard}
      onClick={() => navigate(`/condition/${condition.icd10}`)}
    >
      <div className={styles.cardContent}>
        <div className={styles.conditionContent}>
          <h3 className={styles.conditionName}>{condition.name}</h3>
          <p className={styles.conditionDescription}>{condition.description}</p>
          <div className={styles.conditionMeta}>
            <span className={styles.conditionCode}>ICD-10: {condition.icd10}</span>
            <span>• {condition.prevalence}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSpecialtyCard = (specialty: any) => (
    <div 
      className={styles.resultCard}
      onClick={() => navigate(`/specialty/${specialty.code}`)}
    >
      <div className={styles.cardContent}>
        <div className={styles.specialtyContent}>
          <h3 className={styles.specialtyName}>{specialty.name}</h3>
          <p className={styles.specialtyDescription}>{specialty.description}</p>
          <div className={styles.specialtyMeta}>
            <span className={styles.specialtyCode}>Code: {specialty.code}</span>
            <span>• {specialty.providerCount} providers</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderResult = (result: SearchResultItem) => {
    switch (result.type) {
      case 'list':
        return renderListCard(result.data);
      case 'provider':
        return renderProviderCard(result.data);
      case 'procedure':
        return renderProcedureCard(result.data);
      case 'medication':
        return renderMedicationCard(result.data);
      case 'article':
        return renderArticleCard(result.data);
      case 'condition':
        return renderConditionCard(result.data);
      case 'specialty':
        return renderSpecialtyCard(result.data);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.searchResults}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Searching for "{query}"...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.searchResults}>
        <div className={styles.noResults}>
          <h2>No results found</h2>
          <p>Try adjusting your search terms</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.searchResults}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerContent}>
          <h1 className={styles.searchQuery}>"{query}"</h1>
          <p className={styles.resultCount}>{data.totalResults} results</p>
        </div>
        <button className={styles.filterButton} onClick={() => setShowFilters(true)}>
          <FilterListIcon />
        </button>
      </div>

      <div className={styles.sortingBar}>
        <div className={styles.sortingControls}>
          <span className={styles.sortLabel}>Sort by:</span>
          <div className={styles.sortOptions}>
          <button 
            className={`${styles.sortOption} ${sortBy === 'relevance' ? styles.active : ''}`}
            onClick={() => handleSortChange('relevance')}
          >
            <VerifiedIcon className={styles.sortIcon} />
            Mario Picks
          </button>
          <button 
            className={`${styles.sortOption} ${sortBy === 'cost' ? styles.active : ''}`}
            onClick={() => handleSortChange('cost')}
          >
            <AttachMoneyIcon className={styles.sortIcon} />
            Lowest Cost
          </button>
          <button 
            className={`${styles.sortOption} ${sortBy === 'rating' ? styles.active : ''}`}
            onClick={() => handleSortChange('rating')}
          >
            <StarIcon className={styles.sortIcon} />
            Highest Rated
          </button>
          <button 
            className={`${styles.sortOption} ${sortBy === 'distance' ? styles.active : ''}`}
            onClick={() => handleSortChange('distance')}
          >
            <LocationOnIcon className={styles.sortIcon} />
            Distance
          </button>
          <button 
            className={`${styles.sortOption} ${sortBy === 'rewards' ? styles.active : ''}`}
            onClick={() => handleSortChange('rewards')}
          >
            <CardGiftcardIcon className={styles.sortIcon} />
            Most Rewards
          </button>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.resultsGrid}>
          {(() => {
            // Group results by type for section headers
            const groupedResults = sortedResults.reduce((acc, result) => {
              if (!acc[result.type]) acc[result.type] = [];
              acc[result.type].push(result);
              return acc;
            }, {} as Record<string, SearchResultItem[]>);

            // If results are very mixed (no type dominates), show sections
            const shouldShowSections = Object.keys(groupedResults).length > 1 && 
              !Object.values(groupedResults).some(group => group.length > sortedResults.length * 0.7);

            if (shouldShowSections) {
              // Render with section headers
              const typeOrder = ['provider', 'procedure', 'medication', 'list', 'condition', 'article'];
              return typeOrder.map(type => {
                const results = groupedResults[type];
                if (!results || results.length === 0) return null;
                
                const typeLabels: Record<string, string> = {
                  provider: 'Providers',
                  procedure: 'Procedures', 
                  medication: 'Medications',
                  list: 'Collections',
                  condition: 'Conditions',
                  article: 'Articles'
                };

                const icons: Record<string, JSX.Element> = {
                  provider: <PersonIcon className={styles.sectionIcon} />,
                  procedure: <MedicalServicesIcon className={styles.sectionIcon} />,
                  medication: <MedicationIcon className={styles.sectionIcon} />,
                  list: <ListIcon className={styles.sectionIcon} />,
                  condition: <LocalHospitalIcon className={styles.sectionIcon} />,
                  article: <ArticleIcon className={styles.sectionIcon} />
                };

                const isExpanded = expandedSections.has(type);

                return (
                  <div key={type} className={styles.resultSection}>
                    <div 
                      className={styles.sectionHeader}
                      onClick={(e) => toggleSectionExpansion(type, e)}
                    >
                      {icons[type]}
                      <h3 className={styles.sectionTitle}>
                        {typeLabels[type]} ({results.length})
                      </h3>
                      <button className={styles.expandButton}>
                        {isExpanded ? (
                          <ExpandLessIcon className={styles.expandIcon} />
                        ) : (
                          <ExpandMoreIcon className={styles.expandIcon} />
                        )}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className={styles.sectionContent}>
                        {results.map((result, index) => (
                          <div key={`${result.type}-${result.id}-${index}`}>
                            {renderResult(result)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }).filter(Boolean);
            } else {
              // Render normally without sections
              return sortedResults.map((result, index) => (
                <div key={`${result.type}-${result.id}-${index}`}>
                  {renderResult(result)}
                </div>
              ));
            }
          })()}
        </div>
      </div>

      <SearchFiltersModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={(newFilters) => {
          setFilters(newFilters);
          // TODO: Apply filters to results
        }}
        currentFilters={filters}
        availableTypes={Array.from(new Set(sortedResults.map(r => r.type)))}
      />
    </div>
  );
};

export default SearchResults;